import React, { useState } from "react";
import Logo from "../../../assets/icons/logo.svg";
import { axios } from "axios";
import Modal from "./PricingModal";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { handleSubscription } from "../../../utils/paymentPage";

// Prices are DISPLAYED in USD (matches the marketing site); the actual Razorpay
// charge still uses the plan's INR pricing.*.price.
const USD_PRICE = {
  starter: { monthly: 30, annually: 24 },
  growth: { monthly: 99, annually: 79 },
};

// Curated marketing feature bullets per plan (Starter / Growth). These are the
// displayed lists — actual enforcement lives in the backend plan gating.
const PLAN_FEATURES = {
  starter: [
    "Up to 2 channels",
    "Up to 2,000 members",
    "One-time Channels AI setup",
    "Basic analytics",
    "Web embed & core channels",
    "Email support",
  ],
  growth: [
    "Up to 5 channels",
    "Up to 10,000 members",
    "Bot Studio — community bot",
    "Channels AI — anytime",
    "Advanced analytics",
    "Embed login passthrough (SSO / JWT)",
    "Priority support",
  ],
};

const PricingCard = ({ plan, type }) => {
  const [openInfoIndex, setOpenInfoIndex] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const myData = useSelector((state) => state.myData);
  const business = useSelector((state) => state.business?.business);
  const navigate = useNavigate();

  // The plan the business is already subscribed to — don't offer it again.
  const activePlanId = business?.current_subscription?.planId;
  const isCurrent = isLoggedIn && activePlanId && plan._id === activePlanId;

  const handleInfoOpen = (index) => setOpenInfoIndex(index);
  const handleInfoClose = () => setOpenInfoIndex(null);

  const extractFeatures = () => {
    // Starter / Growth use the curated marketing list above.
    if (PLAN_FEATURES[plan._id]) {
      return PLAN_FEATURES[plan._id].map((name) => ({ name, available: true }));
    }
    const f = plan.features;
    const features = [];
    if (plan._id === "scale") {
      features.push({ name: `Pay as you go`, available: true });
      features.push({ name: `Unlimited Channels`, available: true });
      features.push({ name: `Unlimited Users`, available: true });
      features.push({ name: `Unlimited Newsletters`, available: true });
    }
    if (f.maxChannels && plan._id !== "scale")
      features.push({
        name: `${
          f.maxChannels === 1
            ? `${f.maxChannels} Channel`
            : `Upto ${f.maxChannels} Channels`
        }`,
        available: true,
      });
    if (f.userLimit && plan._id !== "scale")
      features.push({ name: `Total ${f.userLimit} users`, available: true });
    if (f.paidEvents !== undefined)
      features.push({ name: "Paid Events", available: f.paidEvents });
    if (f.autoLogin !== undefined)
      features.push({ name: "Auto Login", available: f.autoLogin });
    if (f.newslettersPerMonth > 0 && plan._id !== "scale")
      features.push({
        name: `${f.newslettersPerMonth} Monthly Newsletters`,
        available: true,
      });
    if (f.integrationSupport !== undefined)
      features.push({
        name: "Integration Support",
        available: f.integrationSupport,
      });
    if (f.communityManager !== undefined)
      features.push({
        name: "Community Manager",
        available: f.communityManager,
      });
    if (f.inAppNotifications !== undefined)
      features.push({
        name: "In-App Notifications",
        available: f.inAppNotifications,
      });

    if (f.analytics) {
      const analyticsList = Object.entries(f.analytics)
        .filter(([_, value]) => value.enabled)
        .map(([_, value]) => value.label);

      if (analyticsList.length) {
        features.push({
          name: f.analyticTitle,
          available: true,
          info: analyticsList,
        });
      } else {
        features.push({ name: "Analytics", available: false });
      }
    }
    return features;
  };

  const featureList = extractFeatures();

  const handleStartClick = (price, planId, type) => {
    if (planId === "scale") return; // custom tier -> contact link, no checkout
    if (!isLoggedIn) {
      navigate("/get-started");
      return;
    }
    // Recurring monthly auto-pay mandate (Razorpay Subscription).
    handleSubscription(myData, {
      planId,
      billingCycle: type,
      planName: plan.name,
    });
  };

  return (
    <div className="bg-theme-pricingBackground rounded-lg p-6 flex flex-col relative space-y-4">
      <div className="flex flex-row justify-between items-center">
        <div className="text-theme-secondaryText text-lg font-light">{plan.name}</div>

        {plan.badge && (
          <div className=" bg-[#CAD83C] text-black text-xs px-2 py-1 rounded-full">
            {plan.badge}
          </div>
        )}
      </div>
      {plan._id !== "scale" && (
        <div className="text-theme-secondaryText text-2xl font-light flex items-baseline space-x-2">
          <span className="font-normal">
            {"$" +
              (type === "monthly"
                ? USD_PRICE[plan._id]?.monthly ?? plan.pricing.monthly.price
                : USD_PRICE[plan._id]?.annually ?? plan.pricing.annually.price)}
          </span>
          <span className="text-xs font-extralight">
            {type === "monthly" ? "/month" : "/mo billed annually"}
          </span>
        </div>
      )}
      {plan._id === "scale" && (
        <div className="text-theme-secondaryText text-2xl font-light flex items-center space-x-2">
          Custom Pricing
        </div>
      )}
      {plan.description && (
        <p className="text-xs text-theme-secondaryText font-extralight">{plan.description}</p>
      )}

      {plan._id !== "scale" && isCurrent && (
        <button
          disabled
          className={`border border-[#CAD83C] text-[#CAD83C] text-sm py-2 rounded-lg font-light cursor-default`}
        >
          ✓ Current plan
        </button>
      )}
      {plan._id !== "scale" && !isCurrent && (
        <button
          className={`border border-theme-chatDivider text-theme-secondaryText text-sm py-2 rounded-lg font-light`}
          onClick={() =>
            handleStartClick(
              type === "monthly"
                ? plan.pricing.monthly.price
                : plan.pricing.annually.price,
              plan._id,
              type
            )
          }
        >
          {activePlanId ? "Switch to this plan" : plan.buttonText}
        </button>
      )}
      {plan._id === "scale" && (
        <a
          href="mailto:reach@channels.social?subject=Talk%20to%20sales"
          className={`border border-theme-chatDivider text-theme-secondaryText text-center text-sm py-2 rounded-lg font-light`}
        >
          {plan.buttonText}
        </a>
      )}

      {plan._id !== "scale" && (
        <a
          href="mailto:reach@channels.social?subject=Talk%20to%20sales"
          className={`text-theme-secondaryText text-center text-sm py-2 font-light`}
        >
          Talk to sales
        </a>
      )}
      <ul className="text-theme-secondaryText font-light text-sm space-y-2 pt-2">
        <p className="text-theme-placeholder text-sm font-light">
          {plan.buttonText === "Get started for free"
            ? "Features:"
            : plan.buttonText === "Talk to sales"
            ? ""
            : "Core Features:"}
        </p>

        {featureList.map(
          (feature, index) =>
            feature.available && (
              <li key={index} className="flex items-center space-x-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 text-theme-secondaryText`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                <span>{feature.name}</span>
                {feature.info && (
                  <div className="relative">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3.5 w-3.5 text-theme-secondaryText cursor-pointer ml-1"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      onMouseEnter={() => handleInfoOpen(index)}
                      onMouseLeave={handleInfoClose}
                      onClick={() => handleInfoOpen(index)}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M12 20c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8z"
                      />
                    </svg>

                    {openInfoIndex === index && (
                      <div className="absolute top-6 left-0 w-40 bg-[#333333]  text-theme-secondaryText font-light text-sm rounded-md shadow-lg p-2 z-50">
                        {feature.info.map((line, i) => (
                          <p key={i} className="text-xs py-1">
                            {line}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </li>
            )
        )}
      </ul>
    </div>
  );
};

export default PricingCard;
